/**
 * InquiryForm Component - Story 7.3: Create Product Detail Page
 * Form for submitting product inquiries with validation
 * Uses React Hook Form + Zod for form management and validation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare, Send, CheckCircle, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  submitProductInquiry,
  MarketplacePublicApiError,
} from '../services/marketplacePublicService';
import {
  inquiryFormSchema,
  type InquiryFormData,
  sanitizePhoneNumber,
  sanitizeMessage,
} from '../schemas/inquiryForm.schema';
import type { InquiryFormProps } from '../types/publicMarketplace.types';

/**
 * InquiryForm Component
 * Allows users to submit inquiries about products
 *
 * Features:
 * - Full form validation with Zod
 * - React Hook Form integration
 * - Real-time validation feedback
 * - Loading states during submission
 * - Success/error messages
 * - Responsive design
 * - Accessible form controls
 *
 * Form Fields:
 * - Name (2-100 characters, letters only)
 * - Email (valid email format)
 * - Phone (Indian phone number format)
 * - Quantity (positive integer)
 * - Message (10-1000 characters, min 3 words)
 *
 * @component
 * @example
 * <InquiryForm
 *   productId="prod-123"
 *   productName="Biogas Equipment"
 *   onSuccess={() => console.log('Inquiry submitted')}
 * />
 */
export const InquiryForm: React.FC<InquiryFormProps> = ({
  productId,
  productName,
  minimumOrderQuantity,
  unit,
  onSuccess,
  onError,
  className,
}) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successInquiryId, setSuccessInquiryId] = useState<string | null>(null);

  // Create dynamic schema that validates minimum quantity if set
  const dynamicSchema = useMemo(() => {
    if (!minimumOrderQuantity) {
      return inquiryFormSchema;
    }

    return inquiryFormSchema.refine(
      (data) => data.quantity >= minimumOrderQuantity,
      {
        message: `Minimum order quantity is ${minimumOrderQuantity} ${unit || 'units'}. You must order at least this amount.`,
        path: ['quantity'],
      }
    );
  }, [minimumOrderQuantity, unit]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InquiryFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      quantity: minimumOrderQuantity || 1,
      message: '',
    },
  });

  /**
   * Navigate to confirmation page after successful submission
   * Wait 1.5 seconds to show success message first
   */
  useEffect(() => {
    if (submitSuccess && successInquiryId) {
      const redirectTimer = setTimeout(() => {
        navigate('/marketplace/inquiry/confirmation', {
          state: {
            inquiryId: successInquiryId,
            productName: productName,
          },
        });
      }, 1500); // Wait 1.5 seconds before redirect

      return () => clearTimeout(redirectTimer);
    }
  }, [submitSuccess, successInquiryId, navigate, productName]);

  /**
   * Handle form submission
   * Sanitizes data and submits to API
   * On success, navigates to confirmation page after 1.5 seconds
   *
   * Error Handling (Story 7.4):
   * - HTTP 429: Rate limit exceeded (too many inquiries)
   * - HTTP 409: Duplicate inquiry (already inquired about product)
   */
  const onSubmit = async (data: InquiryFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);
      setSuccessInquiryId(null);

      // Sanitize data before submission
      const submissionData = {
        productId,
        name: data.name.trim(),
        email: data.email.toLowerCase().trim(),
        phone: sanitizePhoneNumber(data.phone),
        quantity: data.quantity,
        message: sanitizeMessage(data.message),
      };

      // Submit inquiry
      const response = await submitProductInquiry(submissionData);

      if (response.success) {
        setSubmitSuccess(true);
        setSuccessInquiryId(response.inquiryId || null);
        reset(); // Clear form
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response.message || 'Failed to submit inquiry');
      }
    } catch (error) {
      console.error('[InquiryForm] Submission error:', error);

      let errorMessage: string;

      // Check for specific HTTP status codes (Story 7.4)
      if (error instanceof MarketplacePublicApiError) {
        // Handle MarketplacePublicApiError with statusCode
        if (error.statusCode === 429) {
          // Rate limit exceeded
          errorMessage = 'Too many inquiries. Try again in 1 hour.';
        } else if (error.statusCode === 409) {
          // Duplicate inquiry
          errorMessage = 'You already inquired about this product.';
        } else if (error.statusCode === 400) {
          // Check if it's a minimum quantity error
          if (error.message && error.message.includes('Minimum order quantity')) {
            errorMessage = error.message;
          } else {
            errorMessage = error.message || 'Invalid request. Please check your input and try again.';
          }
        } else {
          // Other API errors - use error message
          errorMessage = error.message || 'An error occurred while submitting your inquiry. Please try again.';
        }
      } else if (error instanceof Error) {
        // Handle generic errors
        errorMessage = error.message;
      } else {
        // Handle unknown error types
        errorMessage = 'An error occurred while submitting your inquiry. Please try again.';
      }

      setSubmitError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5 text-primary" />
          Interested in this product?
        </CardTitle>
        <p className="text-sm text-gray-600 mt-1">
          Submit your inquiry and the seller will contact you directly.
        </p>
      </CardHeader>

      <CardContent className="p-6">
        {/* Success Message */}
        {submitSuccess && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-900">
              <span className="font-medium">Inquiry submitted successfully!</span>
              <br />
              The seller will contact you soon via email or phone.
            </AlertDescription>
          </Alert>
        )}

        {/* Error Message */}
        {submitError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-5 w-5" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        {/* Inquiry Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Product Name (Read-only) */}
          <div>
            <Label htmlFor="product-name" className="text-sm font-medium">
              Product
            </Label>
            <Input
              id="product-name"
              type="text"
              value={productName}
              disabled
              className="mt-1.5 bg-gray-50"
            />
          </div>

          {/* Name Field */}
          <div>
            <Label htmlFor="name" className="text-sm font-medium">
              Your Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              className={cn('mt-1.5', errors.name && 'border-red-500')}
              aria-invalid={errors.name ? 'true' : 'false'}
              aria-describedby={errors.name ? 'name-error' : undefined}
              disabled={isSubmitting || submitSuccess}
              {...register('name')}
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-red-600 mt-1">
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              className={cn('mt-1.5', errors.email && 'border-red-500')}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
              disabled={isSubmitting || submitSuccess}
              {...register('email')}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-red-600 mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <Label htmlFor="phone" className="text-sm font-medium">
              Phone Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              type="text"
              inputMode="numeric"
              maxLength={10}
              placeholder="9876543210"
              className={cn('mt-1.5', errors.phone && 'border-red-500')}
              aria-invalid={errors.phone ? 'true' : 'false'}
              aria-describedby={errors.phone ? 'phone-error' : undefined}
              disabled={isSubmitting || submitSuccess}
              {...register('phone', {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                },
              })}
            />
            {errors.phone && (
              <p id="phone-error" className="text-sm text-red-600 mt-1">
                {errors.phone.message}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Enter 10-digit mobile number (e.g. 9876543210)
            </p>
          </div>

          {/* Quantity Field */}
          <div>
            <Label htmlFor="quantity" className="text-sm font-medium">
              Quantity Required <span className="text-red-500">*</span>
            </Label>
            {minimumOrderQuantity && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md mb-2 mt-1.5">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm text-amber-800 font-medium">
                  Minimum order: {minimumOrderQuantity} {unit}
                </span>
              </div>
            )}
            <Input
              id="quantity"
              type="number"
              min="1"
              placeholder="Enter quantity"
              className={cn('mt-1.5', errors.quantity && 'border-red-500')}
              aria-invalid={errors.quantity ? 'true' : 'false'}
              aria-describedby={errors.quantity ? 'quantity-error' : undefined}
              disabled={isSubmitting || submitSuccess}
              {...register('quantity', { valueAsNumber: true })}
            />
            {errors.quantity && (
              <p id="quantity-error" className="text-sm text-red-600 mt-1">
                {errors.quantity.message}
              </p>
            )}
          </div>

          {/* Message Field */}
          <div>
            <Label htmlFor="message" className="text-sm font-medium">
              Your Message <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="message"
              rows={5}
              placeholder="Tell the seller about your requirements, delivery location, or any specific questions..."
              className={cn('mt-1.5 resize-none', errors.message && 'border-red-500')}
              aria-invalid={errors.message ? 'true' : 'false'}
              aria-describedby={errors.message ? 'message-error' : undefined}
              disabled={isSubmitting || submitSuccess}
              {...register('message')}
            />
            {errors.message && (
              <p id="message-error" className="text-sm text-red-600 mt-1">
                {errors.message.message}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Minimum 10 characters, at least 3 words
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isSubmitting || submitSuccess}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : submitSuccess ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Submitted Successfully
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Submit Inquiry
              </>
            )}
          </Button>

          {/* Privacy Notice */}
          <p className="text-xs text-gray-500 text-center mt-4">
            Your contact information will only be shared with the seller.
            <br />
            We respect your privacy and will not use your data for marketing purposes.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default InquiryForm;
